import { Box, HStack, Text, Tooltip } from "@chakra-ui/react";
import { ColumnDef } from "@tanstack/react-table";
import { useMemo } from "react";
import { useNavigate } from "react-router";

import DataCell from "@/common/components/DataCell";
import { DataTable } from "@/common/components/DataTable/DataTable";
import NotAvailableCell from "@/common/components/NotAvailableCell";
import TimestampCell, {
  formatRelativeTime,
} from "@/common/components/TimestampCell";
import { UserDisplay } from "@/common/components/UserDisplay";
import { useEnvironment } from "@/common/contexts/EnvironmentContext";
import { useSafeDataTableStateProps } from "@/common/hooks/useDataTableParamState";
import { useTableDisplayModeParam } from "@/common/hooks/useTableDisplayMode";
import { createSortableColumnHelper } from "@/common/types/reactTableHelpers";
import { relativeTimeBuckets } from "@/common/utils/datetime";
import dayjs from "@/common/utils/dayjs";
import { linkClickModifierHandler } from "@/common/utils/linkClickHandler";
import { CheckIcon } from "@/feature/components/ArchivingComponents";
import { FlagCleanupCell } from "@/feature/components/FlagCleanupCell";
import { FlagNameCell } from "@/feature/components/FlagNameCell";
import { FlagsEmptyState } from "@/feature/components/FlagsEmptyState";
import { NoStage } from "@/feature/components/RolloutStageTag";
import { TableDisplayModePicker } from "@/feature/components/TableDisplayModePicker";
import { FlagWithSubRows } from "@/feature/data/useFlags";
import { useFlagsSearch } from "@/feature/hooks/useFlagsSearch";
import {
  defaultStaleFlagViewColumns,
  groupingStaleFlagsColumns,
  useFlagsTableState,
} from "@/feature/hooks/useFlagsTableState";
import { StageDisplayName } from "@/stage/components/StageDisplayName";
import { useFlag } from "@reflag/react-sdk";
import { FlagHeaderCollectionSortBy } from "@reflag/shared/flagAPI";
import { sortByFlagColumn } from "@reflag/shared/flagSorting";
import { FlagViewDTO } from "@reflag/shared/flagViewAPI";
import { FeatureUrl } from "@reflag/shared/urls";

const columnHelper = createSortableColumnHelper<
  FlagWithSubRows,
  FlagHeaderCollectionSortBy,
  string
>();

type FlagList = {
  data: FlagWithSubRows[];
  metadata?: { sortType: string; viewId: string | undefined };
};

type Props = {
  view: FlagViewDTO;
  data?: FlagList;
  isLoading?: boolean;
  isFetching?: boolean;
  isError?: boolean;
  onRetry?: () => void;
};

function ArchivingChecksCell({
  codeRefsCleanedUp,
  codeRefsMarkedCleanAt,
  codeRefsMarkedCleanUserName,
  lastCheckAt,
  noRecentChecks,
  rolledOutToEveryoneAt,
  stale,
}: {
  codeRefsCleanedUp: boolean;
  codeRefsMarkedCleanAt?: string;
  codeRefsMarkedCleanUserName?: string;
  lastCheckAt?: string;
  noRecentChecks: boolean;
  rolledOutToEveryoneAt?: string;
  stale: boolean;
}) {
  const checks = [
    {
      label: rolledOutToEveryoneAt
        ? `Stale - Rolled out ${formatRelativeTime(dayjs(rolledOutToEveryoneAt), dayjs(), "hour")}`
        : "Not rolled out to everyone",
      passed: stale,
    },
    {
      label: codeRefsCleanedUp
        ? codeRefsMarkedCleanAt
          ? `Marked as done ${formatRelativeTime(dayjs(codeRefsMarkedCleanAt), dayjs(), "day")}${codeRefsMarkedCleanUserName ? ` by ${codeRefsMarkedCleanUserName}` : ""}`
          : "Code scan found no reference to the flag key"
        : "Code references still present",
      passed: codeRefsCleanedUp,
    },
    {
      label: noRecentChecks
        ? "No recent checks"
        : lastCheckAt
          ? `Last check: ${formatRelativeTime(dayjs(lastCheckAt), dayjs(), "hour")}`
          : "No access checks found",
      passed: noRecentChecks,
    },
  ];

  const passedCount = checks.filter((check) => check.passed).length;

  return (
    <HStack align="center" fontSize="sm" h="20px" spacing={2.5}>
      <HStack align="center" spacing={1}>
        {checks.map((check, index) => (
          <Tooltip key={index} label={check.label} hasArrow>
            <Box
              alignItems="center"
              cursor="help"
              display="inline-flex"
              justifyContent="center"
            >
              <CheckIcon size="sm" value={check.passed} />
            </Box>
          </Tooltip>
        ))}
      </HStack>
      <Text fontWeight="medium" lineHeight="1" whiteSpace="nowrap">
        {passedCount}/{checks.length} passed
      </Text>
    </HStack>
  );
}

function getColumns(): ColumnDef<FlagWithSubRows, any>[] {
  return [
    columnHelper.accessor("name", {
      header: "Name",
      cell: FlagNameCell,
      maxSize: 300,
    }),
    columnHelper.accessor("stage", {
      header: "Stage",
      cell: function StageRenderer(cell) {
        const value = cell.getValue();
        if (value == null) {
          return <NoStage fontSize="sm" />;
        }
        return <StageDisplayName fontSize="sm" stage={value} />;
      },
      getGroupingValue: (row) => row.stage?.name,
    }),
    columnHelper.accessor("key", {
      header: "Key",
      cell: (cell) => {
        const value = cell.getValue();
        return (
          <DataCell
            fontFamily="code"
            fontSize="sm"
            type="string"
            value={value}
          />
        );
      },
    }),
    columnHelper.accessor("owner", {
      header: "Owner",
      cell: function OwnerRenderer(cell) {
        const value = cell.getValue();
        if (cell.row.getIsGrouped() && !value) {
          return "No owner";
        }
        if (!value) {
          return <NotAvailableCell />;
        }
        return (
          <UserDisplay _avatar={{ size: "2xs" }} user={value} onlyFirstName />
        );
      },
      getGroupingValue: (row) => row.owner?.name,
    }),
    columnHelper.accessor(
      (row) =>
        Number(row.stale) +
        Number(row.codeRefsCleanedUp) +
        Number(row.noRecentChecks),
      {
        id: "archivingChecks",
        header: "Archive checks",
        enableSorting: true,
        cell: (cell) => {
          const row = cell.row.original;

          return (
            <ArchivingChecksCell
              codeRefsCleanedUp={row.codeRefsCleanedUp}
              codeRefsMarkedCleanAt={row.codeRefsMarkedCleanAt}
              codeRefsMarkedCleanUserName={row.codeRefsMarkedCleanUserName}
              lastCheckAt={row.lastCheckAt}
              noRecentChecks={row.noRecentChecks}
              rolledOutToEveryoneAt={row.rolledOutToEveryoneAt}
              stale={row.stale}
            />
          );
        },
      },
    ),
    columnHelper.accessor("rolledOutToEveryoneAt", {
      header: "Rolled out to everyone",
      enableSorting: true,
      cell: (cell) => {
        const value = cell.getValue();
        return value ? <TimestampCell value={value} /> : <NotAvailableCell />;
      },
    }),
    columnHelper.display({
      id: "cleanup",
      header: "Cleanup",
      enableSorting: false,
      cell: (cell) => <FlagCleanupCell {...cell.row.original} />,
    }),
    columnHelper.accessor("createdAt", {
      header: "Created",
      cell: (cell) => {
        const value = cell.getValue();
        if (cell.row.getIsGrouped()) {
          return relativeTimeBuckets(value);
        }
        return value ? <TimestampCell value={value} /> : <NotAvailableCell />;
      },
      getGroupingValue: (row) => relativeTimeBuckets(row.createdAt, true),
    }),
  ];
}

export function StaleFlagsTable({
  view,
  data,
  isLoading,
  isFetching,
  isError,
  onRetry,
}: Props) {
  const { env } = useEnvironment();
  const navigate = useNavigate();
  const tableState = useFlagsTableState(view);
  const safeTableProps = useSafeDataTableStateProps(tableState);
  const { isEnabled: linearCleanupEnabled } = useFlag(
    "linear-cleanup-automation",
  );
  const columns = useMemo(
    () =>
      getColumns().filter(
        (column) => linearCleanupEnabled || column.id !== "cleanup",
      ),
    [linearCleanupEnabled],
  );
  const defaultColumns = useMemo(
    () =>
      defaultStaleFlagViewColumns.filter(
        (column) => linearCleanupEnabled || column !== "cleanup",
      ),
    [linearCleanupEnabled],
  );
  const [displayMode] = useTableDisplayModeParam();

  const flags = data?.data ?? [];
  const filteredFlags = useFlagsSearch(
    flags,
    {
      enabled: true,
      predicate: (flag) => {
        const query = tableState.searchQuery.toLowerCase();
        return (
          flag.id === tableState.searchQuery ||
          flag.key.toLowerCase().includes(query) ||
          flag.name.toLowerCase().includes(query)
        );
      },
    },
    [tableState.searchQuery],
  );

  return (
    <DataTable
      columns={columns}
      data={filteredFlags}
      defaultColumns={defaultColumns}
      displayMode={displayMode}
      emptyState={
        <FlagsEmptyState
          isError={isError}
          isLoading={isLoading}
          view={view}
          onRetry={onRetry}
        />
      }
      groupingSorter={sortByFlagColumn}
      isFetching={isFetching}
      meta={data?.metadata}
      searchPlaceholder="Search by name, key, or ID"
      tableId="stale-flags"
      toolbarRightActions={() => (
        <TableDisplayModePicker<string>
          groupingColumn={tableState.groupingColumn}
          groupingColumns={groupingStaleFlagsColumns}
          onGroupingColumnChange={tableState.onGroupingColumnChange}
        />
      )}
      variant="clickable"
      canCustomize
      canSearch
      canSort
      hasPersistedState
      onRowClick={(row, e) => {
        const url = FeatureUrl(env, {
          id: row.original.featureId,
          name: row.original.name,
        });
        linkClickModifierHandler(url, (event) => {
          event.preventDefault();
          navigate(url);
        })(e);
      }}
      {...safeTableProps}
    />
  );
}
